import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  ArrowForward as ArrowForwardIcon,
  CloudUpload as CloudUploadIcon,
  PeopleAlt as PeopleAltIcon,
  Star as StarIcon,
  SecurityUpdateGood as SecurityUpdateGoodIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  History as HistoryIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Settings as SettingsIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import activityService from '../../services/activityService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface ActivityFeedProps {
  loading?: boolean;
}

// Activity types for different events
type ActivityType = 'upload' | 'download' | 'share' | 'delete' | 'edit' | 'create' | 'view' | 'access' | 'favorite' | 'settings';

// Activity item interface
interface ActivityItem {
  id: string;
  type: ActivityType;
  user: {
    id: string;
    name: string;
    avatar?: string;
  };
  resource: {
    id: string;
    name: string;
    type: 'file' | 'folder' | 'system';
  };
  timestamp: Date;
  details?: string;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ loading: propLoading = false }) => {
  const theme = useTheme();
  const { user } = useSelector((state: RootState) => state.auth);
  const [tabValue, setTabValue] = useState(0);
  const [activityData, setActivityData] = useState<ActivityItem[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Get icon based on activity type
  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'upload':
        return <UploadIcon fontSize="small" />;
      case 'download':
        return <DownloadIcon fontSize="small" />;
      case 'share':
        return <ShareIcon fontSize="small" />;
      case 'delete':
        return <DeleteIcon fontSize="small" />;
      case 'edit':
        return <EditIcon fontSize="small" />;
      case 'create':
        return <AddIcon fontSize="small" />;
      case 'view':
        return <VisibilityIcon fontSize="small" />;
      case 'access':
        return <LockIcon fontSize="small" />;
      case 'favorite':
        return <StarIcon fontSize="small" />;
      case 'settings':
        return <SettingsIcon fontSize="small" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };
  
  // Get color based on activity type
  const getActivityColor = (type: ActivityType) => {
    switch (type) {
      case 'upload':
      case 'create':
        return theme.palette.success.main;
      case 'download':
      case 'view':
        return theme.palette.info.main;
      case 'share':
        return theme.palette.primary.main;
      case 'delete':
        return theme.palette.error.main;
      case 'edit':
        return theme.palette.warning.main;
      case 'access':
        return theme.palette.secondary.main;
      case 'favorite':
        return '#FF9800'; // Orange
      case 'settings':
        return theme.palette.text.secondary;
      default:
        return theme.palette.primary.main;
    }
  };
  
  // Get descriptive text for activity
  const getActivityText = (activity: ActivityItem) => {
    const { type, resource } = activity;
    
    switch (type) {
      case 'upload':
        return `uploaded ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'download':
        return `downloaded ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'share':
        return `shared ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'delete':
        return `deleted ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'edit':
        return `edited ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'create':
        return `created ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'view':
        return `viewed ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'access':
        return `changed permissions for ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'favorite':
        return `starred ${resource.type === 'file' ? 'a file' : 'a folder'}`;
      case 'settings':
        return `updated account settings`;
      default:
        return `interacted with ${resource.type === 'file' ? 'a file' : 'a folder'}`;
    }
  };
  
  // Map backend activity types to frontend types
  const mapActivityType = (backendType: string): ActivityType => {
    // Map MongoDB/backend activity types to frontend display types
    const typeMap: Record<string, ActivityType> = {
      'file_upload': 'upload',
      'file_download': 'download',
      'file_delete': 'delete',
      'file_update': 'edit',
      'file_view': 'view',
      'file_share': 'share',
      'folder_create': 'create',
      'folder_delete': 'delete',
      'folder_update': 'edit',
      'share_create': 'share',
      'share_delete': 'delete',
      'user_login': 'access',
      'user_logout': 'access',
      'settings_update': 'settings',
      'permission_change': 'access',
      'favorite_add': 'favorite',
      'favorite_remove': 'favorite'
    };

    return typeMap[backendType] || 'view' as ActivityType;
  };

  // Transform backend activity data to UI format
  const transformActivity = (activity: any): ActivityItem => {
    return {
      id: activity._id || activity.id,
      type: mapActivityType(activity.type),
      user: {
        id: activity.userId || user?.id || '',
        name: activity.userName || user?.name || 'Unknown User',
        avatar: activity.userAvatar
      },
      resource: {
        id: activity.resourceId || '',
        name: activity.resourceName || 'Unknown Resource',
        type: (activity.resourceType === 'file' ? 'file' : 
               activity.resourceType === 'folder' ? 'folder' : 'system') as 'file' | 'folder' | 'system'
      },
      timestamp: new Date(activity.timestamp || activity.createdAt),
      details: activity.details
    };
  };
  
  // Fetch activity data from backend
  useEffect(() => {
    const fetchActivities = async () => {
      if (propLoading) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await activityService.getActivities(1, 10);


        
        const transformed = (response.activities || []).map(transformActivity);

        
        setActivityData(transformed);
        setFilteredActivities(transformed);
      } catch (err) {
        console.error('Failed to fetch activities:', err);
        setError('Failed to load activities');
        // Set empty array on error
        setActivityData([]);
        setFilteredActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [propLoading, user?.id]);
  
  // Filter activities based on selected tab
  useEffect(() => {
    if (tabValue === 0) {
      // All activities
      setFilteredActivities(activityData);
    } else if (tabValue === 1) {
      // My activities
      setFilteredActivities(activityData.filter(activity => activity.user.id === user?.id));
    } else if (tabValue === 2) {
      // File activities
      setFilteredActivities(activityData.filter(activity => activity.resource.type === 'file'));
    } else if (tabValue === 3) {
      // Folder activities
      setFilteredActivities(activityData.filter(activity => activity.resource.type === 'folder'));
    }
  }, [tabValue, activityData, user]);

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%', 
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Activity Feed</Typography>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Filter activities">
              <IconButton size="small">
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small">
              <MoreVertIcon />
            </IconButton>
          </Box>
        }
      />
      
      <Divider />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="fullWidth"
          sx={{ 
            minHeight: 48,
            '& .MuiTab-root': {
              py: 1.5,
              minHeight: 48,
            }
          }}
        >
          <Tab label="All" />
          <Tab label="My Activity" />
          <Tab label="Files" />
          <Tab label="Folders" />
        </Tabs>
      </Box>
      
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : (
          <>
            {filteredActivities.length > 0 ? (
              <List sx={{ py: 0 }}>
                {filteredActivities.map((activity) => (
                  <React.Fragment key={activity.id}>
                    <ListItem 
                      sx={{ 
                        px: 2,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          alt={activity.user.name} 
                          src={activity.user.avatar}
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            bgcolor: !activity.user.avatar ? theme.palette.primary.main : undefined 
                          }}
                        >
                          {!activity.user.avatar && <PersonIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography variant="subtitle2" component="span" sx={{ mr: 0.5 }}>
                              {activity.user.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {getActivityText(activity)}
                            </Typography>
                            <Typography 
                              variant="subtitle2" 
                              component="span" 
                              sx={{ 
                                ml: 0.5,
                                color: 'text.primary',
                                '&:hover': {
                                  textDecoration: 'underline',
                                  cursor: 'pointer'
                                }
                              }}
                            >
                              {activity.resource.name}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Chip
                              size="small"
                              icon={
                                <Box sx={{ 
                                  display: 'flex', 
                                  '& svg': { fontSize: '0.875rem', color: 'inherit' }
                                }}>
                                  {getActivityIcon(activity.type)}
                                </Box>
                              }
                              label={activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                mr: 1,
                                color: getActivityColor(activity.type),
                                bgcolor: theme.palette.action.hover,
                                borderColor: 'transparent',
                                '& .MuiChip-icon': {
                                  color: 'inherit',
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{
                          component: 'div'
                        }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No activity found
                </Typography>
              </Box>
            )}
            
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button 
                variant="text" 
                color="primary"
                component={Link}
                to="/activity"
                sx={{ textTransform: 'none' }}
              >
                View All Activity
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed; 